import type { CSSProperties, ElementType, ReactNode } from 'react'

import { createElement, useEffect, useRef } from 'react'

import { gsap, isCompactViewport, prefersReducedMotion, registerGsap } from '@/lib/gsap'

interface FloatProps {
    as?: ElementType
    /** Looping yoyo target offsets. */
    x?: number
    y?: number
    rotate?: number
    /** Seconds for half a cycle (Webflow shapes used ~5.5s). */
    duration?: number
    className?: string
    style?: CSSProperties
    children?: ReactNode
    [key: string]: unknown
}

/**
 * Gentle infinite floating loop for the decorative 3D shapes (cube, cones,
 * torus). Recreates Webflow's continuous "… Image Move" interactions
 * (a-79 / a-81 / a-82 / a-83). Any base transform supplied via `style`
 * (e.g. translateY(-27%)) is preserved and the float oscillates around it.
 */
export const Float = ({
    as = 'img',
    x = 14,
    y = -16,
    rotate = 0,
    duration = 5.5,
    className,
    style,
    children,
    ...rest
}: FloatProps) => {
    const ref = useRef<HTMLElement | null>(null)

    useEffect(() => {
        registerGsap()
        const el = ref.current
        if (!el) return
        if (prefersReducedMotion() || isCompactViewport()) return

        const ctx = gsap.context(() => {
            gsap.to(el, {
                x,
                y,
                rotation: rotate,
                duration,
                ease: 'sine.inOut',
                yoyo: true,
                repeat: -1
            })
        }, el)

        return () => ctx.revert()
    }, [x, y, rotate, duration])

    return createElement(as, { ref, className, style, ...rest }, children)
}
